import React, { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users2,
  UserCircle2,
  ClipboardList,
  Trophy,
  LogOut,
  Shield,
  ShieldCheck,
  Crosshair,
  Telescope,
  Activity,
  HeartPulse,
  Briefcase,
  ClipboardCheck,
  BookOpen,
  CalendarDays,
  CalendarRange,
  Satellite,
  CalendarCheck,
  Stethoscope,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import OfflineBanner from "@/components/OfflineBanner";
import InstallAppButton from "@/components/InstallAppButton";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, test: "nav-dashboard" },
  { to: "/calendario", label: "Calendario", icon: CalendarDays, test: "nav-calendario" },
  { to: "/teams", label: "Equipos", icon: Shield, test: "nav-teams" },
  { to: "/players", label: "Jugadores", icon: UserCircle2, test: "nav-players" },
  { to: "/trainings", label: "Entrenamientos", icon: ClipboardList, test: "nav-trainings" },
  { to: "/horarios", label: "Horarios", icon: CalendarRange, viewerOk: true, test: "nav-horarios" },
  { to: "/matches", label: "Partidos", icon: Trophy, test: "nav-matches" },
  { to: "/rivals", label: "Rivales", icon: Crosshair, test: "nav-rivals" },
  { to: "/scouting", label: "Scouting y mercado", icon: Telescope, adminOnly: true, viewerOk: true, test: "nav-scouting" },
  { to: "/rffm", label: "Motor RFFM", icon: Satellite, adminOnly: true, viewerOk: false, test: "nav-rffm" },
  { to: "/rffm-sync", label: "Sincronización RFFM", icon: CalendarCheck, adminOnly: true, viewerOk: false, test: "nav-rffm-sync" },
  { to: "/coordinador", label: "Coordinador", icon: Briefcase, adminOnly: true, viewerOk: true, test: "nav-coordinador" },
  { to: "/fisioterapia", label: "Fisioterapia", icon: Stethoscope, adminOnly: true, viewerOk: false, test: "nav-fisioterapia" },
  { to: "/eval-entrenadores", label: "Eval. entrenadores", icon: ClipboardCheck, adminOnly: true, viewerOk: true, test: "nav-eval-entrenadores" },
  { to: "/tareas", label: "Tareas", icon: BookOpen, viewerOk: true, test: "nav-tareas" },
  { to: "/fisico", label: "Tests físicos", icon: Activity, test: "nav-fisico" },
  { to: "/cargas", label: "Cargas & Wellness", icon: HeartPulse, test: "nav-cargas" },
  { to: "/coaches", label: "Entrenadores", icon: Users2, adminOnly: true, test: "nav-coaches" },
  { to: "/admins", label: "Administradores", icon: ShieldCheck, adminOnly: true, test: "nav-admins" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("ef-sidebar-collapsed") === "1");

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      localStorage.setItem("ef-sidebar-collapsed", c ? "0" : "1");
      return !c;
    });

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const visibleNav = useMemo(
    () => nav.filter((n) => {
      if (user?.role === "admin") return true;
      if (user?.role === "viewer") return !n.adminOnly || n.viewerOk;
      return !n.adminOnly;
    }),
    [user?.role],
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className={`hidden md:flex ${collapsed ? "w-[72px]" : "w-64"} shrink-0 flex-col bg-white border-r border-slate-200 transition-[width] duration-200`}>
        <div className={`border-b border-slate-100 ${collapsed ? "px-2 py-4" : "px-6 py-6"}`}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
            <img src="/logo.jpg" alt="EF Concepción" className="w-11 h-11 rounded-lg object-contain bg-white border border-slate-200" />
            {!collapsed && (
              <div>
                <div className="font-black text-lg leading-tight" style={{ fontFamily: "Nunito" }}>
                  EF Concepción
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">
                  Madrid · Gestión
                </div>
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={toggleCollapsed}
          data-testid="sidebar-collapse-btn"
          title={collapsed ? "Expandir menú" : "Contraer menú"}
          className={`flex items-center gap-2 mt-2 mb-1 rounded-md text-xs font-bold text-slate-500 hover:bg-slate-100 hover:text-purple-700 transition-colors ${collapsed ? "justify-center mx-2 px-0 py-2.5" : "mx-3 px-3 py-2"}`}
        >
          {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : (<><PanelLeftClose className="h-4 w-4" /> Contraer menú</>)}
        </button>

        <nav className="flex-1 py-2 overflow-y-auto">
          {visibleNav.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.test}
                  title={n.label}
                  className={({ isActive }) =>
                    `flex items-center gap-3 text-sm font-semibold transition-colors ${collapsed ? "justify-center px-0 py-3" : "px-6 py-3"} ${
                      isActive
                        ? "sidebar-active"
                        : "text-slate-600 hover:text-purple-700 hover:bg-purple-50/40"
                    }`
                  }
                >
                  <Icon className={collapsed ? "h-5 w-5" : "h-4 w-4"} />
                  {!collapsed && n.label}
                </NavLink>
              );
            })}
        </nav>

        <div className={`border-t border-slate-100 ${collapsed ? "p-2" : "p-4"}`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-800 grid place-items-center font-bold" title={user?.name} data-testid="current-user-name">
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full px-0"
                onClick={handleLogout}
                title="Salir"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-800 grid place-items-center font-bold">
                  {user?.name?.[0]?.toUpperCase()}
                </div>
                <div className="text-sm min-w-0">
                  <div className="font-semibold truncate" data-testid="current-user-name">
                    {user?.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">
                    {user?.role === "admin" ? "Director" : user?.role === "viewer" ? "Observador" : "Entrenador"}
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" /> Salir
              </Button>
              <div className="mt-2">
                <InstallAppButton />
              </div>
            </>
          )}
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="p-1.5 -ml-1.5 rounded-md hover:bg-slate-100 text-slate-700"
          aria-label="Abrir menú"
          data-testid="menu-toggle-mobile"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo.jpg" alt="EF Concepción" className="w-8 h-8 rounded-lg object-contain bg-white border border-slate-200" />
          <span className="font-black" style={{ fontFamily: "Nunito" }}>EF Concepción</span>
        </div>
        <Button size="sm" variant="outline" onClick={handleLogout} data-testid="logout-button-mobile">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>

      {/* Mobile drawer (full menu) */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="absolute top-0 left-0 bottom-0 w-72 max-w-[85vw] bg-white shadow-xl flex flex-col">
            <div className="px-5 py-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/logo.jpg" alt="EF Concepción" className="w-10 h-10 rounded-lg object-contain bg-white border border-slate-200" />
                <div>
                  <div className="font-black leading-tight" style={{ fontFamily: "Nunito" }}>
                    EF Concepción
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">
                    Madrid · Gestión
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
                aria-label="Cerrar menú"
                data-testid="menu-close-mobile"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 py-3 overflow-y-auto">
              {visibleNav.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    onClick={() => setDrawerOpen(false)}
                    data-testid={`${n.test}-drawer`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-3 text-sm font-semibold transition-colors ${
                        isActive
                          ? "sidebar-active"
                          : "text-slate-600 hover:text-purple-700 hover:bg-purple-50/40"
                      }`
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {n.label}
                  </NavLink>
                );
              })}
            </nav>
            <div className="p-4 border-t border-slate-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-800 grid place-items-center font-bold">
                  {user?.name?.[0]?.toUpperCase()}
                </div>
                <div className="text-sm min-w-0">
                  <div className="font-semibold truncate">{user?.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">
                    {user?.role === "admin" ? "Director" : user?.role === "viewer" ? "Observador" : "Entrenador"}
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm" className="w-full" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Salir
              </Button>
              <div className="mt-2">
                <InstallAppButton />
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 flex justify-around py-2">
        {visibleNav.slice(0, 5).map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`${n.test}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] font-semibold ${
                  isActive ? "text-purple-700" : "text-slate-500"
                }`
              }
            >
              <Icon className="h-5 w-5" />
              {n.label}
            </NavLink>
          );
        })}
      </div>

      <main className="flex-1 min-w-0 pt-16 md:pt-0 pb-20 md:pb-0">
        <OfflineBanner />
        {user?.role === "viewer" && (
          <div
            data-testid="viewer-banner"
            className="w-full bg-purple-700 text-white px-3 py-1.5 text-xs font-semibold flex items-center justify-center gap-2"
          >
            <span aria-hidden>👁</span> Modo observador · solo lectura
          </div>
        )}
        <div className="p-4 md:p-6 xl:p-8 max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
